if (!customElements.get('crowdfunding-progress')) {
  class CrowdfundingProgress extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const configElement = this.querySelector('[data-cf-config]');
      if (!configElement) return;

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        console.warn('Crowdfunding progress: configuration could not be read.', error);
        return;
      }

      this.sales = this.toNonNegativeNumber(this.config.sales);
      this.highestTarget = this.toNonNegativeNumber(this.config.highestTarget);
      this.numberFormatter = new Intl.NumberFormat(this.config.locale || 'ja');
      this.renderProgress(false);
      this.startCountdown();
      this.startApiSync();
    }

    disconnectedCallback() {
      window.clearInterval(this.countdownTimer);
      window.clearInterval(this.apiTimer);
      this.abortController?.abort();
    }

    toNonNegativeNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : 0;
    }

    renderProgress(announce = true) {
      if (this.highestTarget <= 0) return;

      const percent = Math.round((this.sales / this.highestTarget) * 100);
      const cappedPercent = Math.min(100, Math.max(0, percent));
      const formattedSales = this.numberFormatter.format(this.sales);

      const salesElement = this.querySelector('[data-cf-sales]');
      const percentElement = this.querySelector('[data-cf-percent]');
      const fillElement = this.querySelector('[data-cf-fill]');
      const progressbar = this.querySelector('[data-cf-progressbar]');

      if (salesElement) salesElement.textContent = formattedSales;
      if (percentElement) percentElement.textContent = `${this.numberFormatter.format(percent)}%`;
      if (fillElement) fillElement.style.width = `${cappedPercent}%`;
      if (progressbar) {
        progressbar.setAttribute('aria-valuenow', String(Math.min(this.sales, this.highestTarget)));
        progressbar.setAttribute('aria-valuetext', `${formattedSales}${this.config.unit}、${percent}%`);
      }

      this.querySelectorAll('.crowdfunding-progress__marker[data-target]').forEach((marker) => {
        marker.classList.toggle('is-achieved', this.sales >= this.toNonNegativeNumber(marker.dataset.target));
      });

      this.querySelectorAll('.crowdfunding-progress__goal[data-target]').forEach((goal) => {
        const target = this.toNonNegativeNumber(goal.dataset.target);
        const achieved = this.sales >= target;
        const state = goal.querySelector('[data-cf-goal-state]');
        goal.classList.toggle('is-achieved', achieved);
        if (state) {
          state.textContent = achieved
            ? this.config.achievedLabel
            : `${this.config.remainingLabel}${this.numberFormatter.format(target - this.sales)}${this.config.unit}`;
        }
      });

      this.updateStateLabel();

      if (announce) {
        const liveRegion = this.querySelector('[data-cf-live]');
        if (liveRegion) liveRegion.textContent = `販売数を更新しました。現在${formattedSales}${this.config.unit}です。`;
      }
    }

    updateStateLabel() {
      const stateElement = this.querySelector('[data-cf-state]');
      if (!stateElement) return;

      const endAt = Date.parse(this.config.endAt || '');
      if (Number.isFinite(endAt) && endAt <= Date.now()) {
        stateElement.textContent = this.config.endedLabel;
      } else if (this.sales >= this.highestTarget) {
        stateElement.textContent = this.config.completeLabel;
      } else {
        stateElement.textContent = this.config.activeLabel;
      }
    }

    startCountdown() {
      const countdown = this.querySelector('[data-cf-countdown]');
      const countdownRow = this.querySelector('[data-cf-countdown-row]');
      if (!countdown || !countdownRow) return;

      const endAt = Date.parse(this.config.endAt || '');
      if (!Number.isFinite(endAt)) {
        countdownRow.hidden = true;
        return;
      }

      const update = () => {
        const remaining = endAt - Date.now();
        if (remaining <= 0) {
          countdown.textContent = this.config.endedLabel;
          this.updateStateLabel();
          window.clearInterval(this.countdownTimer);
          return;
        }

        const totalSeconds = Math.floor(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        countdown.textContent = days > 0
          ? `${days}日 ${String(hours).padStart(2, '0')}時間 ${String(minutes).padStart(2, '0')}分`
          : `${String(hours).padStart(2, '0')}時間 ${String(minutes).padStart(2, '0')}分 ${String(seconds).padStart(2, '0')}秒`;
      };

      update();
      this.countdownTimer = window.setInterval(update, 1000);
    }

    startApiSync() {
      if (!this.config.apiEnabled || !this.config.apiUrl) return;

      const refreshSeconds = Math.max(60, this.toNonNegativeNumber(this.config.refreshSeconds) || 300);
      this.fetchSales();
      this.apiTimer = window.setInterval(() => this.fetchSales(), refreshSeconds * 1000);
    }

    async fetchSales() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const url = new URL(this.config.apiUrl, window.location.origin);
        url.searchParams.set('product_id', this.config.productId);
        url.searchParams.set('handle', this.config.handle);

        const sameOrigin = url.origin === window.location.origin;
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: sameOrigin ? 'same-origin' : 'omit',
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const nextSales = payload.sold_count ?? payload.current_sales;
        if (!Number.isFinite(Number(nextSales)) || Number(nextSales) < 0) {
          throw new Error('The response did not contain a valid sold_count.');
        }

        const normalizedSales = this.toNonNegativeNumber(nextSales);
        if (normalizedSales !== this.sales) {
          this.sales = normalizedSales;
          this.renderProgress(true);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Crowdfunding progress: sales sync failed; the fallback value remains visible.', error);
        }
      }
    }
  }

  customElements.define('crowdfunding-progress', CrowdfundingProgress);
}
